import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { normaliseDecimal } from '@/features/finance/lines'
import { useCaseInvoices } from '@/features/finance/api/useInvoices'
import { useCaseDebitNotes } from '@/features/finance/api/useDebitNotes'
import {
  MOCK_AGING,
  MOCK_APPROVALS,
  MOCK_BALANCES,
  MOCK_PAYMENTS,
  MOCK_TRANCHES,
} from '@/mocks/finance'
import { USE_MOCKS } from '@/mocks/fixtures'
import { MOCK_ENTITIES } from '@/mocks/directories'
import {
  toCaseDocumentFromDebitNote,
  toCaseDocumentFromInvoice,
} from '@/types/domain'
import type { AllocationSplitDraft } from '@/features/finance/components/PaymentAllocationSheet'
import type { PaymentDraft } from '@/features/finance/components/FinanceFormSheets'
import type { NewInvoicePayload } from '@/features/finance/invoice'
import type {
  AgingBucket,
  Allocation,
  AllocationPayload,
  ApprovalRequest,
  CaseDocument,
  CaseTrancheRow,
  CounterpartyBalance,
  DebitNote,
  DebitNotePayload,
  ExpenseLine,
  Invoice,
  Payment,
  PaymentMethod,
  PaymentPayload,
} from '@/types/domain'

/* Finance holds AR/AP aging, counterparty balances, payments, rate-change
 * approvals, and invoices and debit notes. All five are filterable by date
 * range (FR-53) and the range control is SHARED, not reinvented per view —
 * which is why the range is a page-level param threaded into each hook rather
 * than local state inside each panel. */

export function useAging(range: string) {
  return useQuery<AgingBucket[]>({
    queryKey: queryKeys.finance.aging(range),
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_AGING
        : (
            await api.get<{ items: AgingBucket[] }>('/finance/aging', {
              params: { range },
            })
          ).items,
  })
}

export function useBalances(range: string) {
  return useQuery<CounterpartyBalance[]>({
    queryKey: queryKeys.finance.balances(range),
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_BALANCES
        : (
            await api.get<{ items: CounterpartyBalance[] }>('/finance/balances', {
              params: { range },
            })
          ).items,
  })
}

/** The cross-case payment log the Money page lists (Story 4.5).
 *
 * The real `GET /finance/payments`, Finance/Super-admin guarded server-side —
 * not the fixture rows this hook used to read out of `internal/showcase`. */
export function usePayments(range: string) {
  return useQuery<Payment[]>({
    queryKey: queryKeys.finance.payments({ range }),
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_PAYMENTS
        : (
            await api.get<{ items: Payment[] }>('/finance/payments', {
              params: { range },
            })
          ).items,
  })
}

/** The payments on ONE case, each with its allocations and its remaining
 *  unallocated balance.
 *
 *  Under the FINANCE key rather than a case-detail one, for the reason
 *  `useCaseDocuments` gives: recording or allocating already invalidates
 *  `finance.all`, and a second key under `cases` would be a second cache of the
 *  same rows that nothing invalidates — a case section quietly showing a
 *  payment as unallocated after somebody allocated it from the Money page. */
export function useCasePayments(caseId: string) {
  return useQuery<Payment[]>({
    queryKey: queryKeys.finance.payments({ caseId }),
    // An empty id is a caller that resolved no case (or gated the read off),
    // not a case with no payments — useCaseTranches' rule, applied here the
    // day a caller started passing one.
    enabled: caseId !== '',
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_PAYMENTS.filter((payment) => payment.caseId === caseId)
        : (await api.get<{ items: Payment[] }>(`/cases/${caseId}/payments`)).items,
  })
}

/** The expected receivable tranches on ONE case (FR-32, FR-33, Story 4.3).
 *
 * What the allocation sheet picks from. Until `finance.Service.WithTrancheAllocations`
 * registered a target source there was nothing on the server that could resolve
 * an allocation target at all, which is why that sheet took the identifier by
 * hand; a tranche is now the one kind that resolves, so it is the one kind the
 * sheet offers.
 *
 * UNDER THE FINANCE KEY, not a case-detail one, and that is the load-bearing
 * choice here. A tranche's remaining balance moves when an allocation lands, and
 * allocating invalidates `finance.all` — so this list refreshes with it. Read off
 * the composed case detail instead (`CaseMoney.tranches`) and the picker would
 * keep offering a tranche somebody had just settled, because nothing invalidates
 * the case-detail cache on an allocation.
 *
 * THE KEY IS COMPOSED HERE rather than added to `queryKeys.finance`, because
 * `lib/query.ts` belongs to no story in this pass. It is built FROM `finance.all`
 * so the invalidation above reaches it; when somebody owns that file this should
 * move into the registry beside `payments`, which is the same shape asked about a
 * different resource. */
export function useCaseTranches(caseId: string) {
  return useQuery<CaseTrancheRow[]>({
    queryKey: [...queryKeys.finance.all, 'tranches', { caseId }],
    // A payment always belongs to a case (`Payment.caseId`), so an empty id here
    // is a caller that has not resolved one yet rather than a case with none.
    enabled: caseId !== '',
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_TRANCHES.filter((tranche) => tranche.caseId === caseId).map(
            ({ caseId: _caseId, ...tranche }) => tranche,
          )
        : (
            await api.get<{ items: CaseTrancheRow[] }>(`/cases/${caseId}/tranches`)
          ).items,
  })
}

/** The company's OWN legal entities, for the "which of ours received it" field.
 *
 * Session-scoped (`/settings/entities`), deliberately not the Super-admin
 * `/admin/entities` the settings screen reads: that payload carries bank
 * details, which the server serves to that screen and to no other (AD-6), and
 * a Finance user is refused it outright. A picker that 403s is a field nobody
 * can fill. Only the id and the name are read here — everything else on the
 * entity is somebody else's screen. */
export function useIssuingEntityOptions() {
  return useQuery<{ id: string; name: string }[]>({
    queryKey: queryKeys.issuingEntities(),
    queryFn: async () => {
      const items = USE_MOCKS
        ? MOCK_ENTITIES
        : (await api.get<{ items: { id: string; name: string }[] }>('/settings/entities'))
            .items
      return items.map((entity) => ({ id: entity.id, name: entity.name }))
    },
  })
}

/** The sheet's draft as the server takes it.
 *
 * The amount stays a decimal STRING all the way through (AD-9) — normalised
 * only for the separators a Russian keyboard produces, never through Number().
 * The `1 234,56` a typist enters and the `1234.56` the server stores are the
 * same sum; parsing to a float to get between them is how the cent goes.
 *
 * Nothing about the BOOKING is sent: no applied rate, no USD figure. The server
 * books at the case's registration-date basis out of the FX cache and refuses
 * loudly when the rate for that date is missing (AD-20) — a rate offered from
 * here would be a second source for a number that must have exactly one. */
export function toPaymentPayload(draft: PaymentDraft): PaymentPayload {
  return {
    caseId: draft.caseId,
    reference: draft.reference.trim(),
    counterpartyName: draft.counterpartyName.trim(),
    direction: draft.direction,
    method: draft.method,
    issuingEntityId: draft.issuingEntityId,
    amount: {
      amount: normaliseDecimal(draft.amount),
      currency: draft.currency,
    },
    /* The transfer fee is OMITTED when nothing was typed, never sent as a zero.
     * Absent means "no fee was recorded"; a 0.00 would read on a report as a
     * transfer that cost the company nothing, which is a claim nobody made —
     * and the server refuses a stored zero outright (migration 0058).
     *
     * The currency falls back to the payment's, because a blank one in the
     * draft MEANS "the payment's own" — that is what the sheet's select renders
     * too, so what was on screen is what is sent. Both halves travel together or
     * neither does; the server refuses a half-answered pair by field. */
    ...(draft.feeAmount.trim() === ''
      ? {}
      : {
          fee: {
            amount: normaliseDecimal(draft.feeAmount),
            currency: draft.feeCurrency === '' ? draft.currency : draft.feeCurrency,
          },
        }),
    receivedOn: draft.receivedOn,
  }
}

/** Books one payment against the log (AD-11: it lands unallocated).
 *
 * Not optimistic. A payment is money, so UX-DR-24's confirmed-write rule
 * applies squarely: the row appears once the server has booked it, never
 * before. The sheet stays open on `pending` and stays open holding the draft
 * if the server refuses, so nothing typed is lost to a failed save. */
export function useRecordPayment() {
  const queryClient = useQueryClient()
  return useMutation<Payment, Error, PaymentPayload>({
    mutationFn: async (payload) => {
      if (USE_MOCKS) {
        // The fixture array is module state, so unshifting the row is what
        // makes the sheet behave offline the way the endpoint does. The
        // booking is invented rather than left blank: offline there is no FX
        // cache to ask, and a 1:1 rate on a non-USD payment is visibly a
        // fixture rather than a plausible wrong number.
        const created: Payment = {
          id: `local-pay-${MOCK_PAYMENTS.length + 1}`,
          ...payload,
          issuingEntityName:
            MOCK_ENTITIES.find((entity) => entity.id === payload.issuingEntityId)?.name ??
            '',
          appliedRate: '1.0000',
          rateDate: payload.receivedOn,
          usd: payload.amount.amount,
          allocated: { amount: '0.00', currency: payload.amount.currency },
          unallocated: payload.amount,
          allocations: [],
          /* `POST /finance/payments` is the FINANCE route (`finance.payment.record`),
             and a payment recorded by the role that confirms is born confirmed
             (0059, L3). Offline there is no caller to ask, so the fixture states
             the answer that route always gives. */
          status: 'confirmed',
        }
        MOCK_PAYMENTS.unshift(created)
        return created
      }
      return api.post<Payment>('/finance/payments', payload)
    },
    onSuccess: (payment) => {
      // The whole finance surface moves when cash lands — balances and aging
      // are computed from the same payments this row joins, so refreshing the
      // log alone would leave the other tabs quietly disagreeing with it.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      // And the CASE, which is a different tree entirely — see `invalidateCase`.
      invalidateCase(queryClient, payment.caseId)
    },
  })
}

/** The allocation sheet's rows as the server takes them.
 *
 * Every split travels in ONE request. The server takes each ceiling check under
 * `FOR UPDATE` inside a single transaction and commits only if all of them pass
 * (AD-11); sending three requests would let two land and the third be refused,
 * leaving a payment half-applied to paper the user meant to settle together.
 *
 * The row's `key` is dropped here — it is React's, never the wire's — and the
 * amount stays a decimal string, normalised for a comma keyboard exactly as a
 * payment's is. */
export function toAllocationPayload(
  splits: AllocationSplitDraft[],
  currency: string,
): AllocationPayload {
  return {
    splits: splits.map((split) => ({
      targetKind: split.targetKind,
      targetId: split.targetId.trim(),
      amount: { amount: normaliseDecimal(split.amount), currency },
    })),
  }
}

/** Applies a payment to the documents it settles (AD-11).
 *
 * Emphatically NOT optimistic, and this is the epic's own example of where
 * UX-DR-24 bites hardest. The conservation law — Σ splits ≤ the payment,
 * Σ allocations ≤ each target's total — can only be evaluated by the server,
 * under row locks, against sums another Finance user may be changing in the
 * same instant. A row written into the cache before that answer comes back
 * would be a receivable the client shows as settled and the ledger does not,
 * and the refusal that follows would arrive after the screen had already said
 * yes. So nothing is written here: success invalidates and the confirmed rows
 * arrive from the server that accepted them.
 *
 * The response carries the whole PAYMENT rather than the new allocation rows,
 * because its `allocated` and `unallocated` figures move with them and a client
 * that recomputed the split itself would be doing money arithmetic (AD-9). */
export function useAllocatePayment() {
  const queryClient = useQueryClient()
  return useMutation<Payment, Error, { paymentId: string; payload: AllocationPayload }>({
    mutationFn: async ({ paymentId, payload }) => {
      if (USE_MOCKS) {
        const payment = MOCK_PAYMENTS.find((row) => row.id === paymentId)
        if (payment === undefined) throw new Error(`no payment ${paymentId}`)

        /* Offline there is no target registry to resolve a label against, so
           the id is echoed with its kind rather than dressed up as "tranche 2
           of 2" — a fixture label that looks resolved would suggest a lookup
           this build cannot do. The FX settlement difference is zero for the
           same reason: it is the gap between two booked rates, and offline
           there is only one. */
        const created: Allocation[] = payload.splits.map((split, index) => ({
          id: `local-alloc-${payment.allocations.length + index + 1}`,
          paymentId,
          targetKind: split.targetKind,
          targetId: split.targetId,
          targetLabel: `${split.targetKind} ${split.targetId}`,
          amount: split.amount,
          appliedRate: payment.appliedRate,
          rateDate: payment.rateDate,
          usd: split.amount.amount,
          fxSettlementDifference: '0.0000',
          allocatedAt: new Date().toISOString(),
        }))

        // The two figures are recomputed together from the rows, so the fixture
        // cannot drift into showing a split that does not add up.
        payment.allocations = [...payment.allocations, ...created]
        const allocated = payment.allocations.reduce(
          (sum, row) => sum + Number(row.amount.amount),
          0,
        )
        payment.allocated = {
          amount: allocated.toFixed(2),
          currency: payment.amount.currency,
        }
        payment.unallocated = {
          amount: (Number(payment.amount.amount) - allocated).toFixed(2),
          currency: payment.amount.currency,
        }
        return payment
      }

      return (
        await api.post<{ payment: Payment }>(`/payments/${paymentId}/allocations`, payload)
      ).payment
    },
    onSuccess: (payment) => {
      /* Same reach as recording a payment: an allocation is what moves a
         receivable, so balances and aging are answering a different question
         the moment it lands. Invalidating the payment keys alone would leave
         the other tabs disagreeing with the row the user just created. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      /* The case id comes off the ANSWER rather than off the variables, which
         carry a payment id and a list of splits and no case at all. The
         response is the whole payment for its own reason (see above), and it
         names the case the money moved against. */
      invalidateCase(queryClient, payment.caseId)
    },
  })
}

/* ── Paying a carrier: one act, one request ──────────────────────────────────
 *
 * Three acts used to be three requests from two sheets: record the actual cost
 * line (because a plan is not a debt and nothing can be allocated to it),
 * record the payment, allocate the payment to the line. Each could fail after
 * the one before it had landed, which for money is worse than a refusal — a
 * booked payment with no allocation, or an actual cost line recognised against
 * an invoice that never arrived.
 *
 * `POST /cases/:id/legs/:legId/carrier-payment` does all three in ONE
 * transaction (spec L5). Everything here is therefore a single mutation with a
 * single refusal, and the client states no figure the server derives: no
 * allocation, no booking, no status. The status in particular is the SERVER's
 * (L3) — it follows the recorder's capability, and a client that named one
 * would be granting itself the confirmation it is supposed to be waiting for.
 */

/** The debt to recognise, when the truck has no actual cost line yet.
 *
 *  `planLineId` names the PLANNED line to realise — the agreed rate — and the
 *  server refuses one that is not a planned line of this case, leg and vehicle
 *  (`ErrPlanNotOnLeg`) or that already carries an actual (`ErrPlanAlreadyRealised`).
 *  Absent means there is no plan at all, which is legal: the line is created
 *  unplanned, and `vehicleId` is then what attributes it to a truck.
 *
 *  `comment` lands in `remarks` (AD-6-suppressed) and is REQUIRED when the
 *  amount exceeds the plan (L2). The rule is stated in the form as well as on
 *  the server: an over-plan cost with no reason is a variance nobody can
 *  explain three weeks later, and finding that out after a refusal costs the
 *  typist the whole draft. */
export interface CarrierDebtDraft {
  planLineId?: string
  vehicleId?: string
  amount: string
  currency: string
  comment?: string
}

/** The whole act, as `POST /cases/:id/legs/:legId/carrier-payment` takes it.
 *
 *  EXACTLY ONE of `expenseLineId` and `debt` (`ErrCarrierDebtOrLine`): either
 *  the truck's cost is already a fact and the payment settles it, or the debt is
 *  being recognised in the same breath. The client decides which, because the
 *  form is the thing that knows — it is holding the leg's money block, with the
 *  plan and the actual side by side. The server never guesses.
 *
 *  The payment carries no `caseId` and no `direction`: both are the server's
 *  from the path and from the act. «Оплатить перевозчику» is outbound by
 *  construction, and a body that could say otherwise is a body that can book
 *  the client's money as a carrier payment. */
export interface CarrierPaymentPayload {
  expenseLineId?: string
  debt?: CarrierDebtDraft
  payment: Omit<PaymentPayload, 'caseId' | 'direction'>
}

/** What the endpoint answers: the payment as booked, and the ACTUAL cost line
 *  it settled — the one that already existed, or the one just recognised. */
export interface CarrierPaymentResult {
  payment: Payment
  expenseLine: ExpenseLine
}

/** Pays one truck: recognise the debt, record the payment, allocate it (L5).
 *
 *  Not optimistic, for every reason `useRecordPayment` and `useAllocatePayment`
 *  give at once, and one more of its own: the allocation ceiling is checked
 *  server-side under row locks, and its refusal rolls back the cost line and the
 *  payment too. A row drawn here would have to be un-drawn three ways.
 *
 *  The invalidation is the WIDEST in this file, and deliberately so — this one
 *  request moves a cost line, a payment and an allocation:
 *
 *    · `finance.all` reaches the cross-case payment log, the balances, the aging
 *      and the case's own payment list (which hangs under `finance.payments`);
 *    · `invalidateCase` reaches the case detail, its money block and — by prefix
 *      — `cases.leg(caseId, legId)`, the composed read the direction page's own
 *      four tiles come from. That last one is the point: the desk presses this
 *      button ON the direction page, and «Оплачено» has to move under them. */
export function usePayCarrier() {
  const queryClient = useQueryClient()
  return useMutation<
    CarrierPaymentResult,
    Error,
    { caseId: string; legId: string; payload: CarrierPaymentPayload }
  >({
    mutationFn: ({ caseId, legId, payload }) =>
      api.post<CarrierPaymentResult>(
        `/cases/${caseId}/legs/${legId}/carrier-payment`,
        payload,
      ),
    onSuccess: (_result, { caseId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      invalidateCase(queryClient, caseId)
    },
  })
}

/** Confirms one recorded payment — Finance's second pair of eyes (L3, L7).
 *
 *  Idempotent server-side: confirming an already-confirmed payment answers the
 *  row as it stands rather than moving `confirmedAt`, so a double press tells
 *  the reader the first press worked instead of rewriting when it happened.
 *
 *  The case comes off the ANSWER, like an allocation's: the request carries a
 *  payment id and nothing else, and the response is the whole payment because
 *  its status and its confirmation timestamp both moved. */
export function useConfirmPayment() {
  const queryClient = useQueryClient()
  return useMutation<Payment, Error, string>({
    mutationFn: (paymentId) => api.post<Payment>(`/payments/${paymentId}/confirm`),
    onSuccess: (payment) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      invalidateCase(queryClient, payment.caseId)
    },
  })
}

/** Deletes a PENDING payment, with its allocations and a stated reason (L8).
 *
 *  This is what «отклонить» means here: there is no rejected status, because a
 *  rejected row that stayed on the ledger is a figure every sum then has to
 *  remember to exclude. The reason is mandatory and goes into the audit trail —
 *  the row is gone, so the trail is the only place left that can say why.
 *
 *  THE RECOGNISED DEBT SURVIVES. Deleting the payment removes the payment and
 *  its allocations; the actual cost line stays, because the carrier is still
 *  owed the money and the record of the payment was the thing that was wrong.
 *
 *  The case id travels in the VARIABLES rather than off the answer, unlike the
 *  confirm above: a 204 carries nothing to read it from, and the case still has
 *  to be refreshed — a direction page holding «Оплачено» from before the
 *  deletion is the same stale figure by a different route. */
export function useDeletePayment() {
  const queryClient = useQueryClient()
  return useMutation<void, Error, { paymentId: string; caseId: string; reason: string }>({
    mutationFn: ({ paymentId, reason }) =>
      api.delete<void>(`/payments/${paymentId}`, { body: { reason } }),
    onSuccess: (_void, { caseId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      invalidateCase(queryClient, caseId)
    },
  })
}

/** What a PENDING payment may be corrected to — the amendable fields only.
 *
 *  NO caseId, direction, receivedOn or currency: the last two are the facts
 *  every booking on the row and its allocations hangs off, so the wire not
 *  carrying them is the client half of the enforcement (the server's
 *  `PaymentAmendment` is the other). `amount` is therefore a bare decimal
 *  string — a figure with no currency beside it cannot restate the one the
 *  row already holds. The fee keeps its pair, because its currency IS
 *  amendable. */
export interface PaymentAmendPayload {
  reference: string
  counterpartyName: string
  method: PaymentMethod
  issuingEntityId: string
  amount: string
  fee?: { amount: string; currency: string }
}

/** Corrects a PENDING payment in place (the third fate beside confirm and
 *  reject). A confirmed payment is refused server-side — the way to change a
 *  checked fact is a correcting entry, not an UPDATE. */
export function useAmendPayment() {
  const queryClient = useQueryClient()
  return useMutation<
    Payment,
    Error,
    { paymentId: string; caseId: string; payload: PaymentAmendPayload }
  >({
    mutationFn: ({ paymentId, payload }) =>
      api.put<Payment>(`/payments/${paymentId}`, payload),
    onSuccess: (payment) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
      invalidateCase(queryClient, payment.caseId)
    },
  })
}

export function useApprovals() {
  return useQuery<ApprovalRequest[]>({
    queryKey: queryKeys.finance.approvals(),
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_APPROVALS
        : (await api.get<{ items: ApprovalRequest[] }>('/finance/approvals')).items,
  })
}

/** Approves or rejects one pending rate-change request (FR-36). The decided
 *  request leaves the pending list, so success is an invalidation, not a merge.
 *
 *  `caseId` travels with the decision and is not read by the request: it is
 *  there so the CASE can be refreshed beside the queue. An approved rate change
 *  is the agreed rate the case's money is judged against (AD-10), so a decision
 *  taken on the Money page and a case screen open in another tab would otherwise
 *  disagree about the ceiling until something else happened to refetch. */
export function useDecideApproval() {
  const queryClient = useQueryClient()
  return useMutation<
    void,
    Error,
    { id: string; caseId: string; decision: 'approve' | 'reject' }
  >({
    mutationFn: async ({ id, decision }) => {
      if (USE_MOCKS) {
        // The fixture array is module state, so dropping the row is what makes
        // the button behave offline the way the showcase endpoint does.
        const index = MOCK_APPROVALS.findIndex((approval) => approval.id === id)
        if (index !== -1) MOCK_APPROVALS.splice(index, 1)
        return
      }
      await api.post<void>(`/finance/approvals/${id}/decision`, { decision })
    },
    onSuccess: (_void, { caseId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.finance.approvals(),
      })
      invalidateCase(queryClient, caseId)
    },
  })
}
/* ── The Documents surface ───────────────────────────────────────────────────
 *
 * `useFinanceDocuments`, `useCaseDocuments(caseNumber)`, `useRaiseDocument` as
 * it was and `toDocumentPayload` are all GONE. Every one of them spoke to
 * `/api/finance/documents`, which is `internal/showcase` — a fixture held in the
 * server process's memory. A document raised through them was stored nowhere and
 * could not be found again, and the archive that has existed for some time was
 * called from no file in this client (issue #27).
 *
 * What replaces them reads the REAL paper: client invoices from
 * `/cases/:id/invoices` and debit notes from `/cases/:id/debit-notes`, merged
 * into one `CaseDocument[]` on this side. The two reads live in `useInvoices.ts`
 * and `useDebitNotes.ts` and are CALLED here rather than re-implemented, which
 * is the load-bearing part: their keys hang off `queryKeys.cases.detail(caseId)`
 * and their write mutations seed them, so a second fetch of the same rows under
 * a `finance` key would be a second cache that nothing invalidates — the case
 * section quietly showing a draft somebody issued from the Money page.
 *
 * ── The three stages, and which hook is which (00-decisions §1) ─────────────
 *
 * A document is DRAFT, then RENDERED, then ISSUED, and the three are three
 * separate server calls because they are three separate decisions:
 *
 *   raise    `useRaiseDocument`      opens the draft
 *   render   `useRenderDocument`     archives the PDF — the bytes that will be sent
 *   issue    `useIssueDocument`      mints the number and makes it binding
 *
 * Generating is not issuing (UX-DR-15), and the temptation to collapse them into
 * one call with a flag is exactly what `document.Handlers.Issue`'s comment
 * refuses: a flag is something a client sets by accident.
 *
 * ── `documentId` is the whole state machine ─────────────────────────────────
 *
 * There is ONE signal for "is the paper on screen current", and it is whether
 * the record carries a `documentId`. The server clears that column on EVERY
 * draft write — `TouchInvoice`, `UpdateInvoiceHeader`, `TouchDebitNote`,
 * `UpdateDebitNote` all set it to NULL — so:
 *
 *   documentId present  →  a CURRENT render exists; Issue is available
 *   documentId absent   →  there is no current render; Issue is refused
 *
 * That single field answers both of this screen's awkward questions: Issue is
 * unavailable without a render, and a document amended after being rendered
 * shows the live draft again rather than yesterday's PDF beside today's figures.
 * Do NOT invent a second signal — a client-side "dirty since render" flag would
 * be a second answer to a question the server already answers, and the two
 * disagree the first time somebody edits the note in another tab. The server
 * refuses the issue regardless (`ErrInvoiceNotRendered`); this is the
 * affordance, not the guard.
 */

/** The paper raised against ONE case — its invoices and its debit notes.
 *
 *  Takes the case ID, not the case number. The fixture keyed on the number
 *  because it had nothing better: it answered the whole archive and filtered it
 *  client-side on the one field a document carried. The real reads are nested
 *  under the case's id, which is also the id the row scope is checked against.
 *
 *  TWO queries rather than one, because there is no cross-case archive list to
 *  ask — `GET /api/documents` REQUIRES a source, and the two documents live in
 *  two tables with two different anonymity boundaries. They are the two hooks
 *  the editors already use, called here so that both surfaces share one cache
 *  entry per resource. Merging is this hook's only job.
 *
 *  The composite it returns mimics a `useQuery` result closely enough for the
 *  section's existing loading / error / empty branches — `data`, `isPending`,
 *  `isError`, `error`, `refetch` — because the alternative is every consumer
 *  learning to combine two query states, which is how one of them ends up
 *  rendering an empty list while the other half is still in flight.
 *
 *  `isPending` is either being pending and `isError` is either failing, both
 *  deliberately pessimistic: a section that showed the invoices while the debit
 *  notes were still loading would announce "3 documents" and then quietly become
 *  5, and one that showed them while the debit-note read had FAILED would state
 *  that a case has no debit notes when the truth is that nobody could ask. */
export function useCaseDocuments(caseId: string) {
  const invoices = useCaseInvoices(caseId)
  const debitNotes = useCaseDebitNotes(caseId)

  const data = useMemo(() => {
    if (invoices.data === undefined || debitNotes.data === undefined) return undefined
    return [
      ...invoices.data.map(toCaseDocumentFromInvoice),
      ...debitNotes.data.map(toCaseDocumentFromDebitNote),
    ].sort(compareNewestFirst)
  }, [invoices.data, debitNotes.data])

  return {
    data,
    isPending: invoices.isPending || debitNotes.isPending,
    isError: invoices.isError || debitNotes.isError,
    /* The invoice read's failure wins when both fail, arbitrarily but
       consistently — one message on screen rather than two, and the same one
       every time so a person reporting it describes something reproducible. */
    error: invoices.error ?? debitNotes.error,
    refetch: () => Promise.all([invoices.refetch(), debitNotes.refetch()]),
  }
}

/** Newest first, by the date the document states about ITSELF.
 *
 *  The invoice date and the note date, not the issue date: a draft has no issue
 *  date at all, and sorting on one would pile every draft at one end of the list
 *  regardless of when it was written. Both are date-only `YYYY-MM-DD` (AD-19),
 *  which sorts correctly as text — no Date object is constructed here, because
 *  parsing a date-only string into one drags in a timezone that the string
 *  deliberately does not have.
 *
 *  The id breaks the tie. Several documents legitimately share a date — FR-61
 *  invoices a case in tranches — and a comparator that returned 0 for them would
 *  leave their order to whichever list responded first, so the section would
 *  reshuffle itself on a refetch that changed nothing. */
function compareNewestFirst(a: CaseDocument, b: CaseDocument): number {
  if (a.documentDate !== b.documentDate) {
    return a.documentDate < b.documentDate ? 1 : -1
  }
  return a.id < b.id ? 1 : -1
}

/** What raising a document takes, whichever kind it is.
 *
 *  A discriminated union rather than one flattened payload with optional halves,
 *  because the two creates genuinely differ: an invoice sends a HEADER and the
 *  server prefills its lines from the accepted revision's printed charges
 *  (AD-15), while a debit note's charges travel WITH the create because a demand
 *  with no stated reason is a document nobody pays. A single shape with both
 *  sets optional would make "invoice with lines" and "debit note without them"
 *  expressible in the type, and both are refused by the server. */
export type RaiseDocumentPayload =
  | { kind: 'invoice'; caseId: string; invoice: NewInvoicePayload }
  | { kind: 'debit-note'; caseId: string; note: DebitNotePayload }

/** Raises one invoice or debit note against a case. It opens as a DRAFT.
 *
 *  ONE mutation for both screens (00-decisions §4). The case's Documents section
 *  and the Money page raise through the same sheet and the same call; what the
 *  case adds is a filter and a prefill, because the case number, the client and
 *  the issuing entity are facts that page already knows and making somebody
 *  retype them is how an invoice ends up filed against the wrong case.
 *
 *  Not optimistic, and for a sharper reason than a payment's: the FX rate is
 *  resolved server-side against the economic date (AD-20) and the totals are
 *  computed there (AD-9). A row written into the cache ahead of the response
 *  would carry figures this client invented, on a document.
 *
 *  Deliberately NOT seeded into the detail caches the way the editors' writes
 *  are. The response is mapped to the view shape the caller renders, and the
 *  full record it came from would have to be threaded through a discriminated
 *  return to reach `useInvoices`' seed — so the list is invalidated instead and
 *  the new draft arrives from the read that owns it. One extra round trip on the
 *  least frequent action on the screen, against one fewer place a cache can be
 *  written from. */
export function useRaiseDocument() {
  const queryClient = useQueryClient()
  return useMutation<CaseDocument, Error, RaiseDocumentPayload>({
    mutationFn: async (payload) => {
      if (payload.kind === 'invoice') {
        const invoice = await api.post<Invoice>(
          `/cases/${payload.caseId}/invoices`,
          payload.invoice,
        )
        return toCaseDocumentFromInvoice(invoice)
      }
      const note = await api.post<DebitNote>(
        `/cases/${payload.caseId}/debit-notes`,
        payload.note,
      )
      return toCaseDocumentFromDebitNote(note)
    },
    onSuccess: (_document, { caseId }) => invalidateCase(queryClient, caseId),
  })
}

/** Which record a render, an issue or a preview is about.
 *
 *  The kind is carried rather than inferred from the id, because nothing about a
 *  uuid says which table it came from and a client that guessed would post an
 *  invoice's id to the debit-note route and read the 404 as "deleted". */
export interface DocumentRef {
  caseId: string
  kind: 'invoice' | 'debit-note'
  id: string
}

/** Renders the record into the archive — the "Generate" press (FR-63, AD-18).
 *
 *  The server lays out the PDF, puts the bytes in object storage and links the
 *  archive row to the record. THOSE bytes are what the screen shows next and
 *  what the recipient is eventually sent: the preview and the issued artifact
 *  are the same file, which is the sentence `document.Handlers.Preview` exists
 *  to honour. An HTML approximation laid out on this side is a draft and is
 *  labelled one; it is never presentable as the document.
 *
 *  Answers with the whole record, so the fresh `documentId` — the signal every
 *  affordance on this screen reads — arrives with the response rather than a
 *  refetch later. */
export function useRenderDocument() {
  const queryClient = useQueryClient()
  return useMutation<CaseDocument, Error, DocumentRef>({
    mutationFn: (ref) => writeToRecord(ref, 'document'),
    onSuccess: (_document, { caseId }) => invalidateCase(queryClient, caseId),
  })
}

/** Issues the record — the distinct second action (UX-DR-15, AD-12).
 *
 *  Through the RECORD's own route, `POST /cases/:id/invoices/:id/issue` or the
 *  debit-note twin — never `POST /api/documents/:id/issue`. That route exists
 *  and answers 409 for these two kinds, naming the route to use instead: issuing
 *  mints a number from the entity's series and stamps the record inside one
 *  transaction, and the archive knows nothing about either. A caller holding the
 *  invoice capability would otherwise sail through the archive's door into a
 *  half-issue.
 *
 *  Refused, not ignored, on a second press — both here and on the server. The
 *  caller pressed a button twice and needs to be told the FIRST press worked, so
 *  the refusal is surfaced as "this was already issued" rather than swallowed;
 *  told nothing, they press it again. Issuing is irreversible (AD-24), so the
 *  client confirms before calling this. That confirmation is an affordance and
 *  not a guard: the irreversibility is enforced by the server's WHERE clause. */
export function useIssueDocument() {
  const queryClient = useQueryClient()
  return useMutation<CaseDocument, Error, DocumentRef>({
    mutationFn: (ref) => writeToRecord(ref, 'issue'),
    onSuccess: (_document, { caseId }) => {
      invalidateCase(queryClient, caseId)
      /* Issuing is the moment a demand becomes a receivable, and aging and
         balances are computed from exactly those. They are CROSS-CASE figures
         under the finance key, so the case-detail invalidation above cannot
         reach them — and a Money page left holding the old buckets would age a
         document it had just watched become real. Rendering does not do this:
         it writes bytes to storage and moves no ledger figure. */
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
    },
  })
}

/** POSTs to one of the two records' action routes and maps the answer.
 *
 *  Both routes on both documents answer the WHOLE record, which is what lets the
 *  two mutations above share this: the kind decides the path and the mapper, and
 *  nothing else about them differs. */
function writeToRecord(
  { caseId, kind, id }: DocumentRef,
  action: 'document' | 'issue',
): Promise<CaseDocument> {
  if (kind === 'invoice') {
    return api
      .post<Invoice>(`/cases/${caseId}/invoices/${id}/${action}`)
      .then(toCaseDocumentFromInvoice)
  }
  return api
    .post<DebitNote>(`/cases/${caseId}/debit-notes/${id}/${action}`)
    .then(toCaseDocumentFromDebitNote)
}

/** Refreshes everything one finance write moved on this case.
 *
 *  ONE invalidation, and it reaches further than it looks. Both document lists,
 *  both detail families AND the direction pages are keyed off
 *  `queryKeys.cases.detail(caseId)` — invalidation matches by prefix — so this
 *  one call refreshes the invoices, the debit notes, the case's own money read
 *  and `queryKeys.cases.leg(caseId, legId)` beneath it. That is the point. A
 *  document raised or issued here moves the Money page too, and that is the
 *  reason the Documents section is a VIEW of the archive rather than a second
 *  one: the two surfaces answer different questions about the same rows, and a
 *  section that refreshed only its own list would let them disagree in front of
 *  the reader.
 *
 *  It is called `invalidateCase` rather than `invalidateCaseDocuments` because
 *  the payment writes need it too, and they need it for the sharper version of
 *  the same failure. `queryKeys.finance.all` and `queryKeys.cases.all` are two
 *  separate trees: paying a haulier from a direction and allocating that
 *  payment to a truck's cost line refreshed every finance tab and left
 *  «Оплачено» and «Остаток» on the direction's own page — and on the case's
 *  direction card, which is where the desk reads them — showing the figures
 *  from before the payment (story 6 AC 3). */
function invalidateCase(
  queryClient: ReturnType<typeof useQueryClient>,
  caseId: string,
) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.cases.detail(caseId) })
}

/** A short-lived signed link to an archived artifact — `{url, expiresAt}`. */
export interface DocumentPreview {
  url: string
  expiresAt: string
}

/** The signed URL for one archived document (AD-18, 00-decisions §2).
 *
 *  NEVER CACHED, and every option below says so in a different way. The link is
 *  pre-signed and dies after five minutes, and it dies on purpose: AD-18 gives
 *  it that expiry precisely so a link copied out of a browser is worthless
 *  before it can be shared. A hook that held it for the default 30 seconds of
 *  freshness would be merely wrong; one that held it for an hour would hand a
 *  person a dead link and tell them the document is broken.
 *
 *  So — `staleTime: 0`, which is `useOfferPreview`'s precedent on the quote
 *  screen and taken for a related reason: the user's loop here is render → look
 *  → amend → render, and a cached answer would show them the document they just
 *  replaced. `gcTime: 0` on top of it, which `useOfferPreview` does not need:
 *  the offer preview is structured JSON this client lays out, while this is a
 *  CREDENTIAL, and a credential left sitting in a cache after the viewer closed
 *  is a credential something else can read. It is handed to an <iframe> or an
 *  <object> and never written into an anchor a person could copy out of the
 *  page, and never persisted.
 *
 *  `retry: false`, and this is the one that changes what the screen can say. The
 *  `document` module is NIL-ABLE: on a deployment with no object storage
 *  configured, `main.go` never constructs its service and `routes.go` never
 *  mounts its routes, so this endpoint answers 404 rather than 500. A 404 here
 *  therefore means "the archive is not available on this deployment", NOT "no
 *  paper exists" — the caller must render that as such, because an empty frame
 *  would say there is no document when the truth is that there is no bucket.
 *  Retrying that answer twice would just delay the message by two round trips;
 *  the global retry rule already declines to retry a decision the server has
 *  made, and this states it locally because it is load-bearing here.
 *
 *  `enabled` only with an id, because a record with no current render has no
 *  archive row to ask about and `/documents/undefined/preview` is a 404 that
 *  means something else entirely. The caller's retry after a failed load is an
 *  ordinary `refetch()`: with `staleTime: 0` it goes to the server and comes
 *  back with a URL good for another five minutes, which is AC 5's whole
 *  requirement. */
export function useDocumentPreviewUrl(documentId: string | undefined) {
  return useQuery<DocumentPreview>({
    /* Composed here rather than added to `queryKeys`, the licence
       `useCaseTranches` above takes and for a stronger reason: the registry
       exists so a feature can invalidate a subtree at once, and NOTHING should
       ever invalidate this entry — it is discarded the moment the viewer closes
       it. A key in the registry is an invitation to reach it. */
    queryKey: ['documents', 'preview', documentId] as const,
    enabled: documentId !== undefined && documentId !== '',
    queryFn: () => api.get<DocumentPreview>(`/documents/${documentId}/preview`),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  })
}
